import clsx from 'clsx';
import React from 'react';

type CardPadding = 'sm' | 'md' | 'lg';
type CardElevation = 'none' | 'raised';
type CardAs = 'button' | 'a';

export type CardProps = {
  ariaLabel?: string;
  as?: CardAs;
  children: React.ReactNode;
  className?: string;
  elevation?: CardElevation;
  href?: string;
  onClick?: () => void;
  padding?: CardPadding;
  title?: string | React.ReactNode;
};

function warnIfMisconfigured(as: CardAs | undefined, onClick: (() => void) | undefined, href: string | undefined): void {
  if (process.env.NODE_ENV === 'production') return;
  if (!as) {
    if (typeof onClick === 'function') {
      console.warn('[Card] onClick is ignored unless as="button" is set.');
    }
    return;
  }
  if (as === 'button' && typeof onClick !== 'function') {
    console.warn('[Card] as="button" requires an onClick handler.');
  }
  if (as === 'a' && !href) {
    console.warn('[Card] as="a" requires an href.');
  }
}

function hasTextContent(node: React.ReactNode): boolean {
  if (typeof node === 'string' || typeof node === 'number') return true;
  if (Array.isArray(node)) return node.some(hasTextContent);
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return hasTextContent(node.props.children);
  return false;
}

function warnIfMissingAccessibleName(as: CardAs | undefined, ariaLabel: string | undefined, children: React.ReactNode): void {
  if (process.env.NODE_ENV === 'production' || !as) return;
  if (!ariaLabel && !hasTextContent(children)) {
    console.warn('[Card] Interactive Card (as="button"|"a") has no accessible name. Provide ariaLabel.');
  }
}

function Card({
  ariaLabel,
  as,
  children,
  className,
  elevation = 'none',
  href,
  onClick,
  padding = 'md',
  title,
}: Readonly<CardProps>): JSX.Element {
  warnIfMisconfigured(as, onClick, href);
  warnIfMissingAccessibleName(as, ariaLabel, children);

  const isInteractive = as === 'button' || as === 'a';

  const rootClassName = clsx(
    'card',
    `card--padding-${padding}`,
    {
      'card--elevation-raised': elevation === 'raised',
      'card--interactive': isInteractive,
    },
    className,
  );

  const content = (
    <>
      {title && (
        <div className="card__title">
          <span className="card__title-text">{title}</span>
        </div>
      )}
      <div className="card__content">{children}</div>
    </>
  );

  if (as === 'button') {
    return (
      <button aria-label={ariaLabel} className={rootClassName} onClick={onClick} type="button">
        {content}
      </button>
    );
  }

  if (as === 'a') {
    return (
      <a aria-label={ariaLabel} className={rootClassName} href={href} onClick={onClick}>
        {content}
      </a>
    );
  }

  return (
    <div aria-label={ariaLabel} className={rootClassName}>
      {content}
    </div>
  );
}

export default Card;
